import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Button, Modal } from "react-bootstrap"
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import Forms from "../../../../../services/forms";
import PubSub from "../../../../../services/PubSub";
import SearchBar from "../../../../controls/SearchBar";
import ListStatus from "../../controls/ListStatus";
import Icon from "../../../../controls/Icon";
import { useHasPermission } from "../../../../../common/Hooks";
import { useSharedSection } from "../../SharedSectionContext";
import Utils from "../../../../../utils/utils";
import ConfirmationPrompt from "../../../../controls/ConfirmationPrompt";

const PUBLISHED_DATE_MOMENT_FORMAT = "DD-MMM-yyyy HH:mm";

const SharedSectionVersionHistory = ({
    show,
    onClose
}) => {
    const { objectType, id: deploymentId } = useParams();
    const ref = useRef();
    const history = useHistory();
    const [translate] = useTranslation();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });
    const { sharedSection } = useSharedSection();

    const [searchPrompt, setSearchPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloadError, setHasDownloadError] = useState(false);
    const [availableVersions, setAvailableVersions] = useState([]);
    const [showDeleteVersion, setShowDeleteVersion] = useState(false);

    const [lastFormVersionsParams, setLastFormVersionsParams] = useState({ pageIncrement: 0 });
    const [formVersionsParams, setFormVersionsParams] = useState({ pageIncrement: 0 });
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalPages, setTotalPages] = useState(0);

    const getVersions = useCallback(async (deploymentId, params) => {
        setIsLoading(true);
        setLastFormVersionsParams(params);
        const paginatedResponse = await Forms.getSharedSectionVersions({
            deploymentId,
            sharedSectionGroupingId: sharedSection.sharedSectionGroupingId,
            size: null,
            prev: params.pageIncrement < 0,
            firstCursor: params.firstCursor,
            lastCursor: params.lastCursor,
            getLatest: params.getLatest,
            query: { search: params.search || undefined }
        });

        const response = paginatedResponse?.data;

        if (!paginatedResponse.error && paginatedResponse.data && response && response.data) {
            setAvailableVersions(response.data);
            setTotalPages(Math.ceil(response.paging.count / response.paging.pageSize));
            setCurrentPage(page => {
                let result;
                if (params.getLatest) {
                    result = Math.ceil(response.paging.count / response.paging.pageSize);
                }
                else if (params.resetPaging) {
                    result = 1;
                }
                else if (params.refresh) {
                    result = page;
                }
                else {
                    result = page + params.pageIncrement
                }
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });
        }
        else {
            setHasDownloadError(true);
        }
        setIsLoading(false);
        setFormVersionsParams(null);
    }, [sharedSection.sharedSectionGroupingId]);

    useEffect(() => {
        if (deploymentId && formVersionsParams) {
            getVersions(deploymentId, formVersionsParams);
        }
    }, [getVersions, formVersionsParams, deploymentId]);

    useEffect(() => {
        PubSub.subscribe("refresh-shared-section-versions", () => {
            if (deploymentId && formVersionsParams) {
                getVersions(deploymentId, formVersionsParams);
            }
        });

        return () => {
            PubSub.unsubscribe("refresh-shared-section-versions");
        };
    }, [deploymentId, formVersionsParams, getVersions]);

    const refreshSharedSectionVersion = useCallback((params) => {
        if (!params) {
            setFormVersionsParams({ ...lastFormVersionsParams, refresh: true });
        }
        else {
            setFormVersionsParams({
                pageIncrement: 0,
                firstCursor: params?.resetPaging ? "" : cursors.firstCursor,
                lastCursor: params?.resetPaging ? "" : cursors.lastCursor,
                resetPaging: params?.resetPaging,
                getLatest: !!params?.newEntryId,
                search: searchPrompt
            });
        }
    }, [lastFormVersionsParams, cursors, searchPrompt]);

    const handleSelectVersion = useCallback(({ sharedSectionId }) => {
        history.push(`/${objectType}/${deploymentId}/shared-sections/${sharedSectionId}`);
        onClose();
    }, [history, objectType, deploymentId, onClose]);

    const handleCreateNewVersion = useCallback(async (sharedSection) => {
        const response = await Forms.createSharedSectionVersion({
            deploymentId,
            sharedSectionGroupingId: sharedSection.sharedSectionGroupingId,
            version: sharedSection.version
        });

        if (!response.error) {
            handleSelectVersion({ sharedSectionId: response.data._id });
            PubSub.publish("show_info", translate("OTM_FORMS_SHARED_SECTIONS_CREATE_VERSION_SUCCESS", { newVersion: response.data.version, prevVersion: sharedSection.version }), translate("OTM_INFO"));
        }

        refreshSharedSectionVersion();
    }, [deploymentId, handleSelectVersion, refreshSharedSectionVersion, translate]);

    const handleDeleteVersion = useCallback(async (deletingSharedSectionVersion) => {
        await Forms.deleteSharedSectionVersion({
            deploymentId,
            sharedSectionId: deletingSharedSectionVersion._id
        });

        if (availableVersions.length === 1) {
            history.push(`/${objectType}/${deploymentId}/shared-sections/`);
        }
        else {
            if (sharedSection._id.toString() === deletingSharedSectionVersion._id.toString()) {
                const newAvailableVersions = availableVersions.filter(({ _id }) => _id !== deletingSharedSectionVersion._id);
                if (newAvailableVersions[0]) {
                    history.push(`/${objectType}/${deploymentId}/shared-sections/${newAvailableVersions[0]._id}`);
                }
            }
        }

        refreshSharedSectionVersion();
        setShowDeleteVersion(false);
    }, [deploymentId, history, objectType, availableVersions, sharedSection._id, refreshSharedSectionVersion]);

    const renderStatusIcon = useCallback((item) => {
        const iconMap = {
            ACTIVE: { icon: "CheckCircleOutline", color: "text-secondary" },
            PUBLISHED: { icon: "LockOutlined", color: "text-secondary" },
            IN_PROGRESS: { icon: "CreateOutlined", color: "text-secondary" }
        };

        let status = "IN_PROGRESS";
        if (item.isPublished) { status = "PUBLISHED"; }

        return <div className="col-auto mt-2">
            <Icon icon={iconMap[status].icon} className={`d-block fs-2 ${iconMap[status].color}`} />
        </div>;
    }, []);

    const renderAdditionalInfo = useCallback((item) => {
        let created = translate("OTM_CREATED_INFO")
            .split("{0}")
            .join(moment(item.createdAt).format(PUBLISHED_DATE_MOMENT_FORMAT))
            .split("{1}")
            .join(item.createdBy?.email || translate("OTM_UNKNOWN_USER"));

        let published = "";
        if (item.isPublished) {
            published = translate("OTM_PUBLISHED_INFO")
                .split("{0}")
                .join(moment(item?.publishedAt).format(PUBLISHED_DATE_MOMENT_FORMAT))
                .split("{1}")
                .join(item?.publishedBy?.email || translate("OTM_UNKNOWN_USER"));
        }

        return <>
            <label className="d-block small">{created}</label>
            {published !== "" ? <label className="d-block small">{published}</label> : null}
            {item.publishedMessage ? <label className="d-block small">{item.publishedMessage}</label> : null}
        </>;
    }, [translate]);

    const previous = useCallback(() => {
        setFormVersionsParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor,
            search: searchPrompt
        });
    }, [cursors.firstCursor, cursors.lastCursor, searchPrompt]);

    const next = useCallback(() => {
        setFormVersionsParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor,
            search: searchPrompt
        });
    }, [cursors.firstCursor, cursors.lastCursor, searchPrompt]);

    const renderVersionList = useMemo(() => {
        if (availableVersions && availableVersions.length > 0) {
            return (
                <div className="overflow-hidden d-flex flex-column modal-body-scroll">
                    <div className="list-group flex-grow-1 overflow-x-hidden overflow-y-auto padded-scrollbar">
                        {availableVersions.map((sharedSectionVersion) => (
                            <div className="list-group-item small p-3" key={sharedSectionVersion._id}>
                                <div className="row mt-n2 align-items-center justify-content-end">
                                    {renderStatusIcon(sharedSectionVersion)}
                                    <div className="col mt-2">
                                        <label className="d-block h6">{`${translate("OTM_VERSION")} ${sharedSectionVersion.version}`}</label>
                                        {renderAdditionalInfo(sharedSectionVersion)}
                                    </div>
                                    <div className="col-12 d-lg-none"></div>
                                    <div className="col-auto mt-2">
                                        <div className="row gx-2 mt-n2 align-items-center justify-content-end">
                                            <div className={`col-auto mt-2 ${sharedSectionVersion._id.toString() === sharedSection?._id.toString() ? 'd-none' : ''}`}>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => {
                                                        handleSelectVersion({ sharedSectionId: sharedSectionVersion._id });
                                                    }}
                                                >{translate("OTM_SELECT")}</Button>
                                            </div>
                                            {permissions.update ? <div className="col-auto mt-2">
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => {
                                                        handleCreateNewVersion(sharedSectionVersion);
                                                    }}
                                                >{translate("OTM_REVISE")}</Button>
                                            </div> : null}
                                            {permissions.delete ? <div className={`col-auto mt-2 ${sharedSectionVersion.isPublished ? 'd-none' : ''}`}>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={() => { setShowDeleteVersion(sharedSectionVersion); }}
                                                >{translate("OTM_DELETE")}</Button>
                                            </div> : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );

        }
        else {
            return <ListStatus loading={isLoading} error={hasDownloadError} empty={true} emptyText={translate("OTM_NO_VERSIONS")} ></ListStatus>;
        }
    }, [availableVersions, isLoading, hasDownloadError, sharedSection?._id, permissions, handleSelectVersion, translate, renderStatusIcon, renderAdditionalInfo, handleCreateNewVersion]);

    const renderActiveSelected = useMemo(() => {
        return <div className="mb-3">
            <div className="row mt-n2">
                <div className="col-6 mt-2">
                    <div className="card text-bg-primary h-100">
                        <div className="card-body p-2 text-center">
                            <label className="d-block h6">{translate("OTM_SELECTED_VERSION")}</label>
                            <label className="d-block h3">{sharedSection?.version}</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
    }, [sharedSection?.version, translate]);

    return (
        <Modal
            size={'xl'}
            show={show}
            backdrop={'static'}>
            {showDeleteVersion &&
                <ConfirmationPrompt show={showDeleteVersion} message={translate("OTM_FORMS_SHARED_SECTIONS_DELETE_VERSION_CONFIRM")}
                    onConfirm={() => { handleDeleteVersion(showDeleteVersion) }}
                    onCancel={() => { setShowDeleteVersion(false); }} />
            }
            <Modal.Header>
                <label className="h5">{translate('OTM_FORMS_SHARED_SECTIONS_VERSION_MANAGEMENT')}</label>
            </Modal.Header>

            <Modal.Body>
                {renderActiveSelected}
                <div className="mb-3 d-flex">
                    <SearchBar
                        ref={ref}
                        size="sm"
                        initialValue={searchPrompt}
                        placeholder={translate("OTM_SEARCH")}
                        onChange={(value) => {
                            setSearchPrompt(value);
                            setFormVersionsParams({
                                pageIncrement: 0,
                                search: value,
                                resetPaging: true
                            })
                        }}
                    />
                    <div className="d-flex align-items-center justify-content-end mt-1">
                        <Button
                            className={`ms-1 p-0 border-0`}
                            variant={"outline-primary"}
                            title={translate("OTM_PREVIOUS")}
                            onClick={previous}
                            disabled={currentPage === 1}
                        >
                            <Icon icon={"ChevronLeft"} className="d-block" />
                        </Button>
                        <span style={{ whiteSpace: "nowrap" }}>
                            {totalPages > 0 ? currentPage : 0} / {totalPages}
                        </span>
                        <Button
                            className={`ms-1 p-0 border-0`}
                            variant={"outline-primary"}
                            title={translate("OTM_NEXT")}
                            onClick={next}
                            disabled={!hasNextPage}
                        >
                            <Icon icon={"ChevronRight"} className="d-block" />
                        </Button>
                    </div>
                </div>

                {renderVersionList}
            </Modal.Body>

            <Modal.Footer>
                <Button size='sm' variant='secondary' onClick={onClose}>{translate('OTM_CLOSE')} </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SharedSectionVersionHistory;